import React, { useState, useEffect, useMemo } from "react";
import {
  Badge,
  Button,
  Input,
  Label,
  Progress,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import SectionCard from "Components/Common/SectionCard";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import styles from "../css/dispensing.module.scss";
import {
  AddDispensingDetailsProps,
  ContainerReconciliation,
  Step2FormValues,
  DispensingViewMode,
  SubjectStatusFilter,
} from "../helpers/dispensing.types";
import {
  autoAssignSubjects,
  buildSubjectRows,
  clearAllAllocations,
  clearSubjectAllocation,
  getAvailableForSplit,
  getSubjectContainerSplits,
  recalculateContainers,
  updateSubjectSplit,
} from "../helpers/dispensing.helper";
import { DISPENSING_LOG_PATHS } from "Routes/paths";
import { showToastMessage } from "common/service/CommonService";

interface SplitRow {
  containerId: string;
  qty: number;
}

// Multi-column subject list. Ideally these two rules live in
// dispensing.module.scss as `.subjectMultiCol` — kept here as a scoped
// <style> tag so this file works correctly even before that move happens.
const MULTI_COL_STYLE = `
  .subjectMultiColLayout { column-count: 3; column-gap: 14px; }
  @media (max-width: 768px) { .subjectMultiColLayout { column-count: 1; } }
  @media (min-width: 769px) and (max-width: 1199px) { .subjectMultiColLayout { column-count: 2; } }
  .subjectRowItem { break-inside: avoid; }
`;

const AddDispensingDetails: React.FC<AddDispensingDetailsProps> = ({
  step1Summary,
  editId = null,
  selectedRecord = null,
  onPrevious,
  onCancel,
  isSubmitting = false,
}) => {
  const navigate = useNavigate();

  /* ------------------------------------------------------------------------ */
  /* 1. SUBJECT LIST PREPARATION                                             */
  /* ------------------------------------------------------------------------ */
  const masterSubjectList = useMemo(() => {
    if (
      step1Summary?.subjectList &&
      Array.isArray(step1Summary.subjectList) &&
      step1Summary.subjectList.length > 0
    ) {
      return step1Summary.subjectList.map((s) => {
        // const strVal = String(s).trim().replace(/^-S/, "");
        const strVal = String(s).trim().replace(/^/, "");
        return strVal.padStart(2, "0");
      });
    }
    // Default fallback to 40 subjects if no study list provided
    return Array.from({ length: 40 }, (_, i) => String(i + 1).padStart(2, "0"));
  }, [step1Summary?.subjectList]);

  /* ------------------------------------------------------------------------ */
  /* 2. FORM & LOCAL STATE                                                    */
  /* ------------------------------------------------------------------------ */
  const perSubjectQtyDefault =
    step1Summary?.perSubjectQty ?? selectedRecord?.quantitypersubject ?? 10;

  const {
    control,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<Step2FormValues>({
    defaultValues: {
      subjectno: masterSubjectList,
      persubjectqty: perSubjectQtyDefault,
      extraquantity: null,
      extra_container_id: "",
      extra_remarks: "",
      totalqtyneeded: null,
    },
    mode: "onChange",
  });

  const selectedSubjectNos = watch("subjectno") || [];
  const perSubjectQtyVal = Number(watch("persubjectqty")) || perSubjectQtyDefault;
  const extraQtyVal = watch("extraquantity");
  const extraContainerIdVal = watch("extra_container_id");
  const extraRemarksVal = watch("extra_remarks");


  // Container Reconciliation State
  const [containers, setContainers] = useState<ContainerReconciliation[]>([]);
  const [unallocatedSubjects, setUnallocatedSubjects] = useState<string[]>([]);

  // View toggle: subject-wise is the primary entry screen; container-wise is
  // kept as a secondary reconciliation/audit view.
  const [viewMode, setViewMode] = useState<DispensingViewMode>("subject");

  // Subject-wise view controls
  const [statusFilter, setStatusFilter] = useState<SubjectStatusFilter>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Split Editor Modal State (shared by both views)
  const [editingSplitSubjectId, setEditingSplitSubjectId] = useState<string | null>(null);
  const [splitRows, setSplitRows] = useState<SplitRow[]>([]);

  // Inline Assign Subjects Panel State (container view only)
  const [assigningContainerId, setAssigningContainerId] = useState<string | null>(null);
  const [assignSelectedSubjects, setAssignSelectedSubjects] = useState<string[]>([]);
  const [assignQtyPerSub, setAssignQtyPerSub] = useState<number>(perSubjectQtyVal);

  
  // Options for container selection (available quantity containers only)
  const availableContainerOptions = useMemo(() => {
    return containers
      .filter((c) => {
        const availableForExtra = c.netUsableQty - c.dispensedToSubjects;
        return availableForExtra > 0 || c.containerId === extraContainerIdVal;
      })
      .map((c) => ({
        label: `Container ${c.containerId.replace(/^C/, "")} (${c.remainingQty} available)`,
        value: c.containerId,
      }));
  }, [containers, extraContainerIdVal]);

  const extraDispensingFields: FormFieldConfig[] = useMemo(
    () => [
      {
        name: "extraquantity",
        label: "Extra Quantity",
        type: "number",
        placeholder: "Enter extra quantity",
      },
      {
        name: "extra_container_id",
        label: "Select Container",
        type: "select",
        placeholder: "Select container",
        options: availableContainerOptions,
      },
      {
        name: "extra_remarks",
        label: "Remarks",
        type: "text",
        placeholder: "Enter remarks (e.g. QC sample, wastage)",
      },
    ],
    [availableContainerOptions]
  );
  /* ------------------------------------------------------------------------ */
  /* 3. INITIALIZE CONTAINERS & UNALLOCATED POOL                              */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    const rawContainers = step1Summary?.selectedContainers || [];
    let initialContainers: ContainerReconciliation[] = [];

    if (rawContainers.length > 0) {
      initialContainers = rawContainers.map((cNum: string) => {
        const cId = cNum.startsWith("C") ? cNum : `C${cNum.padStart(2, "0")}`;
        const netUsable =
          step1Summary?.netUsableContainerQuantity && rawContainers.length > 0
            ? Math.round(step1Summary.netUsableContainerQuantity / rawContainers.length)
            : 200;

        return {
          containerId: cId,
          initialQty: netUsable,
          netUsableQty: netUsable,
          dispensedToSubjects: 0,
          extraQty: 0,
          extraReason: "",
          remainingQty: netUsable,
          subjectAllocations: [],
        };
      });
    } else {
      // Demo fallbacks if coming directly or testing
      initialContainers = [
        { containerId: "C01", initialQty: 200, netUsableQty: 200, dispensedToSubjects: 0, extraQty: 0, extraReason: "", remainingQty: 200, subjectAllocations: [] },
        { containerId: "C02", initialQty: 150, netUsableQty: 150, dispensedToSubjects: 0, extraQty: 0, extraReason: "", remainingQty: 150, subjectAllocations: [] },
        { containerId: "C03", initialQty: 200, netUsableQty: 200, dispensedToSubjects: 0, extraQty: 0, extraReason: "", remainingQty: 200, subjectAllocations: [] },
      ];
    }

    setContainers(initialContainers);
    setUnallocatedSubjects(selectedSubjectNos);

    const initialExtraC = initialContainers.find((c) => (c.extraQty || 0) > 0 || c.extraReason);
    if (initialExtraC) {
      setValue("extra_container_id", initialExtraC.containerId);
      setValue("extraquantity", initialExtraC.extraQty || null);
      setValue("extra_remarks", initialExtraC.extraReason || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step1Summary]);

  // Keep unallocated subjects in sync — a subject only counts as allocated
  // once its total EQUALS the required per-subject quantity (see buildSubjectRows).
  useEffect(() => {
    const allSubjectRows = buildSubjectRows(selectedSubjectNos, containers, perSubjectQtyVal);
    const stillUnallocated = allSubjectRows
      .filter((r) => r.status === "unallocated" || r.status === "partial" || r.status === "over")
      .map((r) => r.subjectId);
    setUnallocatedSubjects(stillUnallocated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubjectNos, containers, perSubjectQtyVal]);

  useEffect(() => {
    setAssignQtyPerSub(perSubjectQtyVal);
  }, [perSubjectQtyVal]);

  // Sync Extra Dispensing CommonForm fields to containers state & reduce quantity from selected container
  useEffect(() => {
    setContainers((prev) => {
      if (!prev || prev.length === 0) return prev;
      const numQty = Number(extraQtyVal) || 0;
      const selectedCId = extraContainerIdVal;
      const remarks = extraRemarksVal || "";

      let hasChanged = false;
      const updated = prev.map((c) => {
        if (c.containerId === selectedCId) {
          if (c.extraQty !== numQty || c.extraReason !== remarks) {
            hasChanged = true;
            return { ...c, extraQty: numQty, extraReason: remarks };
          }
        } else {
          if (c.extraQty !== 0 || (c.extraReason && c.extraReason !== "")) {
            hasChanged = true;
            return { ...c, extraQty: 0, extraReason: "" };
          }
        }
        return c;
      });

      if (!hasChanged) return prev;
      return recalculateContainers(updated);
    });
  }, [extraQtyVal, extraContainerIdVal, extraRemarksVal]);

  /* ------------------------------------------------------------------------ */
  /* 4. DERIVED SUBJECT-WISE DATA                                             */
  /* ------------------------------------------------------------------------ */
  const subjectRows = useMemo(
    () => buildSubjectRows(selectedSubjectNos, containers, perSubjectQtyVal),
    [selectedSubjectNos, containers, perSubjectQtyVal]
  );

  const statusCounts = useMemo(() => {
    const counts = { all: subjectRows.length, needsFix: 0, split: 0, allocated: 0 };
    subjectRows.forEach((r) => {
      if (r.status === "unallocated" || r.status === "partial" || r.status === "over") {
        counts.needsFix += 1;
      } else if (r.status === "split") {
        counts.split += 1;
      } else if (r.status === "allocated") {
        counts.allocated += 1;
      }
    });
    return counts;
  }, [subjectRows]);

  const filteredSubjectRows = useMemo(() => {
    return subjectRows.filter((r) => {
      if (statusFilter === "needsFix" && !["unallocated", "partial", "over"].includes(r.status)) {
        return false;
      }
      if (statusFilter === "split" && r.status !== "split") return false;
      if (statusFilter === "allocated" && r.status !== "allocated") return false;
      if (searchTerm && !r.subjectId.toLowerCase().includes(searchTerm.trim().toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [subjectRows, statusFilter, searchTerm]);

  /* ------------------------------------------------------------------------ */
  /* 5. EVENT HANDLERS                                                        */
  /* ------------------------------------------------------------------------ */

  const handleAutoAssign = () => {
    if (unallocatedSubjects.length === 0) {
      showToastMessage("error", "All selected subjects are already fully allocated.");
      return;
    }

    const { containers: updatedContainers, unallocatedSubjects: remaining } = autoAssignSubjects({
      containers,
      unallocatedSubjects,
      perSubjectQty: perSubjectQtyVal,
    });

    setContainers(updatedContainers);

    const assignedCount = unallocatedSubjects.length - remaining.length;
    if (assignedCount > 0) {
      showToastMessage(
        remaining.length === 0 ? "success" : "error",
        remaining.length === 0
          ? `All ${assignedCount} remaining subjects auto-assigned successfully!`
          : `${assignedCount} subjects auto-assigned. ${remaining.length} couldn't be fully covered by remaining container capacity — please assign manually.`
      );
    } else {
      showToastMessage(
        "error",
        "No remaining container has enough capacity to fully cover any unallocated subject."
      );
    }
  };

  const handleClearAllocation = (subjectId: string) => {
    setContainers((prev) => clearSubjectAllocation(prev, subjectId));
    showToastMessage("success", `Cleared allocation for subject ${subjectId}.`);
  };

  const handleResetAll = () => {
    setContainers((prev) => clearAllAllocations(prev));
    showToastMessage("success", "All allocations have been reset.");
  };

  // const handleRemoveSubjectAllocation = (containerId: string, subjectId: string) => {
  //   setContainers((prev) => {
  //     const updated = prev.map((c) => {
  //       if (c.containerId === containerId) {
  //         return {
  //           ...c,
  //           subjectAllocations: c.subjectAllocations.filter((sa) => sa.subjectId !== subjectId),
  //         };
  //       }
  //       return c;
  //     });
  //     return recalculateContainers(updated);
  //   });
  // };

  // const handleOpenAssignPanel = (containerId: string) => {
  //   setAssigningContainerId(containerId);
  //   setAssignSelectedSubjects([]);
  //   setEditingSplitSubjectId(null);
  // };

  // const handleExecuteAssign = () => {
  //   if (!assigningContainerId || assignSelectedSubjects.length === 0) return;

  //   const targetContainer = containers.find((c) => c.containerId === assigningContainerId);
  //   if (!targetContainer) return;

  //   const qtyPerSub = Number(assignQtyPerSub) || perSubjectQtyVal;
  //   const totalNeededForAssign = assignSelectedSubjects.length * qtyPerSub;

  //   if (totalNeededForAssign > targetContainer.remainingQty) {
  //     showToastMessage(
  //       "error",
  //       `Insufficient container capacity (${targetContainer.remainingQty} units available). Please reduce selected subjects or dose.`
  //     );
  //     return;
  //   }

  //   setContainers((prev) => {
  //     const updated = prev.map((c) => {
  //       if (c.containerId === assigningContainerId) {
  //         const newAllocations = [...c.subjectAllocations];
  //         assignSelectedSubjects.forEach((subId) => {
  //           const existing = newAllocations.find((sa) => sa.subjectId === subId);
  //           if (existing) {
  //             existing.qty += qtyPerSub;
  //           } else {
  //             newAllocations.push({ subjectId: subId, qty: qtyPerSub });
  //           }
  //         });
  //         return { ...c, subjectAllocations: newAllocations };
  //       }
  //       return c;
  //     });
  //     return recalculateContainers(updated);
  //   });

  //   setAssigningContainerId(null);
  //   setAssignSelectedSubjects([]);
  //   showToastMessage("success", "Subjects assigned to container.");
  // };

  const handleOpenSplitEditor = (subjectId: string) => {
    setEditingSplitSubjectId(subjectId);
    setAssigningContainerId(null);

    const existingSplits = getSubjectContainerSplits(containers, subjectId);
    if (existingSplits.length > 0) {
      setSplitRows(existingSplits);
    } else {
      setSplitRows([{ containerId: containers[0]?.containerId || "", qty: perSubjectQtyVal }]);
    }
  };

  const handleAddSplitRow = () => {
    const usedContainerIds = new Set(splitRows.map((r) => r.containerId));
    const availableC = containers.find((c) => !usedContainerIds.has(c.containerId));
    const nextCId = availableC ? availableC.containerId : containers[0]?.containerId || "";
    setSplitRows((prev) => [...prev, { containerId: nextCId, qty: 0 }]);
  };

  const handleRemoveSplitRow = (index: number) => {
    setSplitRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveSplit = () => {
    if (!editingSplitSubjectId) return;

    const target = perSubjectQtyVal;
    const totalSplitQty = splitRows.reduce((sum, r) => sum + Number(r.qty || 0), 0);

    if (splitRows.some((r) => !r.containerId)) {
      showToastMessage("error", "Every split row needs a container selected.");
      return;
    }

    const containerIds = splitRows.map((r) => r.containerId);
    if (new Set(containerIds).size !== containerIds.length) {
      showToastMessage("error", "Each container can only appear once in a subject's split.");
      return;
    }

    if (totalSplitQty !== target) {
      showToastMessage(
        "error",
        `Total split quantity (${totalSplitQty} units) must equal the required dose (${target} units).`
      );
      return;
    }

    for (const row of splitRows) {
      const available = getAvailableForSplit(containers, editingSplitSubjectId, row.containerId);
      if (row.qty > available) {
        showToastMessage(
          "error",
          `Container ${row.containerId.replace(/^C/, "")} only has ${available} units available for this subject.`
        );
        return;
      }
    }

    const updatedContainers = updateSubjectSplit({
      containers,
      subjectId: editingSplitSubjectId,
      newSplits: splitRows,
    });

    setContainers(updatedContainers);
    setEditingSplitSubjectId(null);
    showToastMessage("success", `Split saved for subject ${editingSplitSubjectId}.`);
  };


  const onFinalSubmit = () => {
    if (unallocatedSubjects.length > 0) {
      showToastMessage(
        "error",
        `Please fully allocate all ${unallocatedSubjects.length} remaining subject(s) before submitting.`
      );
      return;
    }

    const payload = {
      containers: containers.map((c) => ({
        containerId: c.containerId,
        initialQty: c.initialQty,
        dispensedToSubjects: c.dispensedToSubjects,
        extraQty: c.extraQty,
        extraReason: c.extraReason || "",
        remainingQty: c.remainingQty,
        subjectAllocations: c.subjectAllocations,
      })),
      subjectAllocations: subjectRows.map((r) => ({
        subjectId: r.subjectId,
        totalQty: r.total,
        splits: r.splits,
      })),
      unallocatedSubjects,
    };

    console.log("Final Dispensing Payload:", payload);
    showToastMessage("success", "Dispensing Log completed and submitted successfully!");
    navigate(DISPENSING_LOG_PATHS.LIST);
  };

  /* ------------------------------------------------------------------------ */
  /* 6. COMPUTED TOTALS                                                       */
  /* ------------------------------------------------------------------------ */
  const totalSelectedCount = selectedSubjectNos.length;
  const fullyAllocatedCount = statusCounts.split + statusCounts.allocated;
  const progressPercent =
    totalSelectedCount > 0 ? Math.round((fullyAllocatedCount / totalSelectedCount) * 100) : 0;

  const currentSplitTotal = splitRows.reduce((sum, r) => sum + Number(r.qty || 0), 0);
  const targetSplitQty = perSubjectQtyVal;
  const splitTotalOk = currentSplitTotal === targetSplitQty;

  /* ------------------------------------------------------------------------ */
  /* 7. RENDER HELPERS                                                        */
  /* ------------------------------------------------------------------------ */

  const renderSplitEditorModal = () => (
    <Modal
      isOpen={!!editingSplitSubjectId}
      toggle={() => setEditingSplitSubjectId(null)}
      centered
    >
      <ModalHeader toggle={() => setEditingSplitSubjectId(null)}>
        Editing split for {editingSplitSubjectId}{" "}
        <span className="text-muted fs-13 fw-normal">· needs {targetSplitQty} units total</span>
      </ModalHeader>
      <ModalBody>
        {splitRows.map((row, idx) => {
          const available = editingSplitSubjectId
            ? getAvailableForSplit(containers, editingSplitSubjectId, row.containerId)
            : 0;
          return (
            <div key={idx} className="d-flex align-items-center gap-3 mb-2">
              <Input
                type="select"
                bsSize="sm"
                style={{ width: "220px" }}
                value={row.containerId}
                onChange={(e) => {
                  const val = e.target.value;
                  setSplitRows((prev) =>
                    prev.map((r, i) => (i === idx ? { ...r, containerId: val } : r))
                  );
                }}
              >
                {containers.map((c) => {
                  const avail = editingSplitSubjectId
                    ? getAvailableForSplit(containers, editingSplitSubjectId, c.containerId)
                    : c.remainingQty;
                  return (
                    <option key={c.containerId} value={c.containerId}>
                      Container {c.containerId.replace(/^C/, "")} ({avail} available)
                    </option>
                  );
                })}
              </Input>

              <div className="d-flex align-items-center gap-2">
                <Input
                  type="number"
                  bsSize="sm"
                  style={{ width: "100px" }}
                  value={row.qty}
                  min={0}
                  max={available}
                  onChange={(e) => {
                    const val = Number(e.target.value) || 0;
                    setSplitRows((prev) =>
                      prev.map((r, i) => (i === idx ? { ...r, qty: val } : r))
                    );
                  }}
                />
                <span className="text-muted fs-13">/ {available} units</span>
              </div>

              {splitRows.length > 1 && (
                <Button
                  type="button"
                  color="link"
                  size="sm"
                  className="text-danger p-0 ms-auto"
                  onClick={() => handleRemoveSplitRow(idx)}
                >
                  <i className="ri-delete-bin-line fs-5" />
                </Button>
              )}
            </div>
          );
        })}

        {splitRows.length < containers.length && (
          <Button
            type="button"
            color="outline-secondary"
            size="sm"
            className="rounded-2 border-dashed fs-12 fw-medium text-dark bg-white mt-2"
            onClick={handleAddSplitRow}
          >
            <i className="ri-add-line me-1" /> Add another container
          </Button>
        )}
      </ModalBody>
      <ModalFooter className="d-flex align-items-center justify-content-between">
        <span className={`fw-bold fs-13 ${splitTotalOk ? "text-success" : "text-danger"}`}>
          {currentSplitTotal} / {targetSplitQty} {splitTotalOk ? "✓" : "⚠"}
        </span>
        <div className="d-flex gap-2">
          <Button color="secondary" outline onClick={() => setEditingSplitSubjectId(null)}>
            Cancel
          </Button>
          <Button color="secondary" outline onClick={handleSaveSplit}>
            Save split
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );

  const renderContainerSummaryStrip = () => (
    <div className="d-flex gap-2 mb-3 flex-wrap">
      {containers.map((c) => {
        const isLow = c.remainingQty === 0;
        return (
          <div
            key={c.containerId}
            className="flex-fill border rounded-3 p-2 px-3 bg-white"
            style={{ minWidth: "150px" }}
          >
            <div className="text-secondary fw-medium fs-13">Container {c.containerId.replace(/^C/, "")}</div>
            <div className={`fs-16 fw-bold ${isLow ? "text-danger" : "text-dark"}`}>
              {c.remainingQty}
              <span className="text-muted fs-12 fw-normal"> / {c.netUsableQty} left</span>
            </div>
          </div>
        );
      })}
    </div>
  );

  const statusStyleFor = (status: string) => {
    switch (status) {
      case "unallocated":
        return { rowClass: styles.subjectRowPending, icon: "ri-time-line", iconColor: "text-muted" };
      case "partial":
        return { rowClass: styles.subjectRowPartial, icon: "ri-error-warning-fill", iconColor: "text-warning" };
      case "over":
        return { rowClass: styles.subjectRowOver, icon: "ri-error-warning-fill", iconColor: "text-danger" };
      case "split":
        return { rowClass: styles.subjectRowSplit, icon: "ri-shuffle-line", iconColor: "text-primary" };
      default:
        return { rowClass: styles.subjectRowAllocated, icon: "ri-checkbox-circle-fill", iconColor: "text-success" };
    }
  };

  /** Renders container breakdown pills for a subject row */
  const renderContainerPills = (row: (typeof subjectRows)[number]) => {
    if (row.status === "unallocated") {
      return <span className={styles.pendingText}>Pending allocation</span>;
    }
    if (row.status === "partial") {
      return (
        <>
          {row.splits.map((sp, idx) => (
            <span key={sp.containerId} className={idx % 2 === 0 ? styles.containerPill : styles.containerPillSecondary}>
              {sp.containerId.replace(/^/, "")} · {sp.qty}
            </span>
          ))}
          <span className={`${styles.statusText} text-warning`}>
            {row.total}/{targetSplitQty}
          </span>
        </>
      );
    }
    if (row.status === "over") {
      return (
        <>
          {row.splits.map((sp, idx) => (
            <span key={sp.containerId} className={idx % 2 === 0 ? styles.containerPill : styles.containerPillSecondary}>
              {sp.containerId.replace(/^/, "")} · {sp.qty}
            </span>
          ))}
          <span className={`${styles.statusText} text-danger`}>
            {row.total}/{targetSplitQty} ↑
          </span>
        </>
      );
    }
    // allocated or split — show all container pills
    return row.splits.map((sp, idx) => (
      <span key={sp.containerId} className={idx % 2 === 0 ? styles.containerPill : styles.containerPillSecondary}>
        {sp.containerId.replace(/^/, "")} · {sp.qty}
      </span>
    ));
  };

  const renderSubjectWiseView = () => (
    <>
      <style>{MULTI_COL_STYLE}</style>

      {renderContainerSummaryStrip()}

      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <div className="d-flex gap-2 flex-wrap">
          <span
            role="button"
            onClick={() => setStatusFilter("all")}
            className={`px-3 py-1 rounded-pill fs-12 fw-semibold ${
              statusFilter === "all" ? "bg-primary text-white" : "bg-subtle text-secondary border border-primary-subtle"
            }`}
          >
            All · {statusCounts.all}
          </span>
          <span
            role="button"
            onClick={() => setStatusFilter("needsFix")}
            className={`px-3 py-1 rounded-pill fs-12 fw-semibold ${
              statusFilter === "needsFix"
                ? "bg-warning text-dark"
                : statusCounts.needsFix > 0
                  ? "bg-warning-subtle text-warning-emphasis border border-warning-subtle"
                  : "bg-light-subtle text-muted border"
            }`}
          >
            Needs attention · {statusCounts.needsFix}
          </span>
          <span
            role="button"
            onClick={() => setStatusFilter("split")}
            className={`px-3 py-1 rounded-pill fs-12 fw-semibold ${
              statusFilter === "split"
                ? "bg-primary text-white"
                : "bg-primary-subtle text-primary border border-primary-subtle"
            }`}
          >
            Split · {statusCounts.split}
          </span>
          <span
            role="button"
            onClick={() => setStatusFilter("allocated")}
            className={`px-3 py-1 rounded-pill fs-12 fw-semibold ${
              statusFilter === "allocated"
                ? "bg-success text-white"
                : "bg-success-subtle text-success border border-success-subtle"
            }`}
          >
            Fully allocated · {statusCounts.allocated}
          </span>
        </div>

        <div className="d-flex gap-2">
          <Input
            type="text"
            bsSize="sm"
            placeholder="Search subject…"
            style={{ width: "160px" }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {unallocatedSubjects.length > 0 && (
            <Button type="button" color="primary"  onClick={handleAutoAssign}>
              <i className="ri-magic-line me-1" /> Auto-assign remaining
            </Button>
          )}
          <Button type="button" color="primary" outline onClick={handleResetAll}>
            Reset all
          </Button>
        </div>
      </div>

      <div className="border rounded-3 p-3 bg-white mb-3">
        {filteredSubjectRows.length === 0 ? (
          <div className="text-center text-muted fs-13 py-4">No subjects match this filter.</div>
        ) : (
          <div className="subjectMultiColLayout">
            {filteredSubjectRows.map((row) => {
              const s = statusStyleFor(row.status);
              return (
                <div
                  key={row.subjectId}
                  className={`${styles.subjectRowCard} ${s.rowClass}`}
                >
                  <span className={styles.subjectIdLabel}>
                    {row.subjectId}
                  </span>
                  <div className={styles.containerPillsWrap}>
                    {renderContainerPills(row)}
                  </div>
                  <i className={`${s.icon} ${s.iconColor} ${styles.statusIcon}`} />
                  <div className={styles.subjectActions}>
                    <i
                      role="button"
                      className="ri-edit-line fs-14 text-muted"
                      title="Edit split"
                      onClick={() => handleOpenSplitEditor(row.subjectId)}
                    />
                    {row.total > 0 && (
                      <i
                        role="button"
                        className="ri-close-circle-line fs-14 text-danger"
                        title="Remove allocation"
                        onClick={() => handleClearAllocation(row.subjectId)}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Extra Dispensing Section — rendered via CommonForm */}
      <div className={styles.extraDispensingSection}>
        <div className={styles.extraDispensingHeader}>
          <i className="ri-add-circle-line fs-18 text-primary d-flex align-items-center" />
          <div>
            <h6 className="mb-0 fw-bold text-dark fs-14">Extra Dispensing</h6>
            <span className="text-muted fs-12">
              Record additional quantity dispensed outside subject doses (e.g., QC samples, wastage)
            </span>
          </div>
        </div>
        <CommonForm
          fields={extraDispensingFields}
          control={control}
          errors={errors}
          setValue={setValue}
          getValues={getValues}
          columns={3}
        />
      </div>
    </>
  );

  const renderContainerWiseView = () => (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        {unallocatedSubjects.length > 0 ? (
          <div className={`${styles.unallocatedBanner} flex-grow-1 d-flex align-items-center justify-content-between`}>
            <div className="d-flex align-items-center gap-2">
              <i className="ri-information-fill fs-5 text-primary" />
              <span className="fw-semibold text-dark fs-14">
                <strong>{unallocatedSubjects.length} subjects</strong> not yet fully allocated{" "}
                <span className="text-muted fw-normal">
                  ({unallocatedSubjects.slice(0, 4).join(", ")}
                  {unallocatedSubjects.length > 4 ? "…" : ""})
                </span>
              </span>
            </div>
            <span className="fs-12 text-muted fst-italic">Use Subject view to assign</span>
          </div>
        ) : (
          <span className="text-success fw-semibold fs-13">
            <i className="ri-checkbox-circle-fill me-1" /> All subjects fully allocated
          </span>
        )}
      </div>

      <div className="d-flex flex-column gap-3 mb-4">
        {containers.map((container) => {
          const containerSplitsCount = container.subjectAllocations.length;
          const isExhausted = container.remainingQty <= 0;

          return (
            <div key={container.containerId} className={styles.containerReconcileCard}>
              <div className={styles.containerHeaderRow}>
                <div className="d-flex align-items-center gap-2">
                  <span
                    className={
                      container.remainingQty > 50
                        ? styles.dotBlue
                        : container.remainingQty > 0
                          ? styles.dotGreen
                          : styles.dotRed
                    }
                  />
                  <h6 className="mb-0 fw-bold text-dark fs-15">
                    Container {container.containerId.replace(/^C/, "")}
                  </h6>
                  <span className="text-muted fs-13">Initial {container.initialQty}</span>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <span className="px-3 py-1 rounded-pill fs-12 fw-semibold bg-primary-subtle text-primary border border-primary-subtle">
                    Dispensed {container.dispensedToSubjects}
                  </span>
                  {isExhausted ? (
                    <span className="px-3 py-1 rounded-pill fs-12 fw-semibold bg-danger-subtle text-danger border border-danger-subtle">
                      Exhausted
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-pill fs-12 fw-semibold bg-success-subtle text-success border border-success-subtle">
                      Remaining {container.remainingQty}
                    </span>
                  )}
                </div>
              </div>

              {containerSplitsCount > 0 && (
                <div className={styles.containerChipsRow}>
                  {container.subjectAllocations.map((alloc) => {
                    const isSplit = getSubjectContainerSplits(containers, alloc.subjectId).length > 1;
                    return (
                      <div
                        key={alloc.subjectId}
                        className={isSplit ? styles.subjectChipSplitReadOnly : styles.subjectChipReadOnly}
                      >
                        <span>
                          {alloc.subjectId} · {alloc.qty} {isSplit ? "(split)" : ""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Extra quantity — read-only display */}
              {(container.extraQty > 0 || container.extraReason) && (
                <div className="d-flex align-items-center gap-2 mt-2 pt-2 border-top border-light-subtle px-3 pb-2">
                  <Label className="mb-0 fs-13 text-dark" style={{ minWidth: "100px" }}>
                    Extra dispensed :
                  </Label>
                  <span className="fs-13 fw-semibold text-dark">{container.extraQty || 0} units</span>
                  {container.extraReason && (
                    <span className="fs-13 fst-italic">( {container.extraReason} )</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );

  /* ------------------------------------------------------------------------ */
  /* 8. RENDER COMPONENT                                                      */
  /* ------------------------------------------------------------------------ */
  return (
    <form onSubmit={handleSubmit(onFinalSubmit)}>
      <SectionCard
        index={1}
        icon="ri-user-heart-line"
        title="Subject Selection & Dose Reconciliation"
        subtitle="Select subjects and distribute required doses across unsealed containers"
        rightContent={
          <div className="d-flex border rounded-2 overflow-hidden bg-white">
            <button
              type="button"
              className={`px-4 py-1 fs-13 fw-semibold border-0 ${
                viewMode === "subject" ? "bg-primary text-white" : "bg-white text-primary"
              }`}
              onClick={() => setViewMode("subject")}
            >
              Subject view
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 fs-13 fw-semibold border-0 border-start ${
                viewMode === "container" ? "bg-primary text-white" : "bg-white text-primary"
              }`}
              onClick={() => setViewMode("container")}
            >
              Reconciliation view
            </button>
          </div>
        }
      >
        <div className="mb-4 border rounded-3 p-3 bg-light-subtle">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Label className="fw-semibold text-dark mb-0 fs-14">
              Subject Selection Pool <span className="text-danger">*</span>
              <Badge color="primary" pill className="ms-2 px-2.5 py-1">
                {selectedSubjectNos.length} Selected
              </Badge>
            </Label>
            {masterSubjectList.length > 0 && (
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  color="link"
                  size="sm"
                  className="p-0 text-decoration-none fw-medium"
                  onClick={() => setValue("subjectno", masterSubjectList, { shouldValidate: true, shouldDirty: true })}
                >
                  Select All
                </Button>
                <span className="text-muted">|</span>
                <Button
                  type="button"
                  color="link"
                  size="sm"
                  className="p-0 text-decoration-none text-danger fw-medium"
                  onClick={() => setValue("subjectno", [], { shouldValidate: true, shouldDirty: true })}
                >
                  Clear All
                </Button>
              </div>
            )}
          </div>

          <Controller
            name="subjectno"
            control={control}
            rules={{ required: "At least one Subject No. must be selected" }}
            render={({ field: { value = [], onChange } }) => (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {masterSubjectList.map((subject: string) => {
                  const isChecked = value.includes(subject);
                  return (
                    <div
                      key={subject}
                      className={`px-2.5 py-1 rounded-pill d-flex align-items-center justify-content-center cursor-pointer user-select-none transition-all ${
                        isChecked
                          ? "bg-primary-subtle text-primary border border-primary-subtle fw-semibold shadow-xs"
                          : "bg-white text-muted fw-normal border border-light-subtle opacity-75"
                      } ${styles.subjectPill}`}
                      onClick={() => {
                        if (isChecked) {
                          onChange(value.filter((s: string) => s !== subject));
                        } else {
                          onChange([...value, subject]);
                        }
                      }}
                    >
                      {subject}
                    </div>
                  );
                })}
              </div>
            )}
          />
        </div>

        {viewMode === "subject" ? renderSubjectWiseView() : renderContainerWiseView()}

        <div className={`mt-0 ${styles.progressBarContainer}`}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="fw-semibold text-dark fs-14">Total subjects fully allocated</span>
            <span className="fw-bold text-dark fs-15">
              {fullyAllocatedCount} / {totalSelectedCount}
            </span>
          </div>

          <Progress value={progressPercent} color="primary" className="mb-2" style={{ height: "6px" }} />

          <div className="d-flex align-items-center justify-content-between">
            {unallocatedSubjects.length > 0 ? (
              <span className="text-danger fw-semibold fs-13">
                {unallocatedSubjects.length} subjects still need attention
              </span>
            ) : (
              <span className="text-success fw-semibold fs-13">
                <i className="ri-checkbox-circle-fill me-1" /> All subjects fully allocated
              </span>
            )}
            {unallocatedSubjects.length > 0 && (
              <span className="text-danger fw-semibold fs-13">Fix before submitting</span>
            )}
          </div>
        </div>
      </SectionCard>

      {renderSplitEditorModal()}

      {/* Sticky Action Footer */}
      <div className={formStyles.stickyFooter}>
        <div className="d-flex justify-content-end gap-2 w-100">
          <Button
            type="button"
            color="primary"
            outline
            onClick={onPrevious}
          >
            Back
          </Button>
          <Button
            type="submit"
            color="primary"
            disabled={isSubmitting || unallocatedSubjects.length > 0}
          >
            {isSubmitting ? "Submitting..." : "Submit dispensing"}
          </Button>
        </div>
      </div>
      
    </form>
  );
};

export default AddDispensingDetails;